<!-- eslint-disable vue/multi-word-component-names -->
<template>
	<div
		class="sidebar z-70 h-full flex-col overflow-y-auto border-r border-[#E9EEF2] bg-white px-4 py-5 transition-all"
		:class="
			collapsed
				? 'hidden md:flex md:w-fit'
				: 'absolute w-full max-w-[240px] md:relative'
		"
	>
		<div class="flex h-full flex-col justify-between gap-6">
			<div class="w-full">
    
				<div v-if="route.name === 'services-tracker'">
					<a
						href="https://www.wonsulting.com/services"
						target="_blank"
					>
						<won-button
							v-tooltip="{
								content: 'See our services',
								delay: { show: displayCollapsed ? 500 : 999999, hide: 100 }
							}"
							class="w-full"
						>
							<ArrowTopRightOnSquareIcon
								v-if="displayCollapsed"
								class="size-4"
							/>
							<span v-else>See our services</span>
						</won-button>
					</a>
				</div>
    
                
				<won-button 
					v-else-if="['interview-ai', 'interview-ai-interviews', 'interview-ai-create'].includes(activeRoute)"
					id="create-new-interview"
					v-tooltip="{ content: 'Start New Interview', delay: { show: displayCollapsed ? 500 : 999999, hide: 100 } }"
					class="w-full"
					@click="startNewInterviewHandler"
				>
					+{{ displayCollapsed ? '' : ' Start New Interview' }}
				</won-button>
     
				<div
					v-else-if="!['cover-letters.index', 'cover-letters.edit', 'home'].includes(activeRoute)"
					class="cursor-pointer"
					@click="createNewResumeHandler"
				>
					<won-button
						v-tooltip="{content: 'Create New Resume', delay: { show: displayCollapsed ? 500 : 999999, hide: 100 }}"
						class="w-full whitespace-nowrap focus:outline-0"
					>
						+{{ displayCollapsed ? '' : ' Create New Resume' }}
					</won-button>
				</div>
    
				<div
					v-else-if="activeRoute !== 'home'"
					class="cursor-pointer"
					@click="createNewCoverLetterHandler"
				>
					<won-button
						v-tooltip="{content: 'New Cover Letter', delay: { show: displayCollapsed ? 500 : 999999, hide: 100 }}"
						class="w-full focus:outline-0"
					>
						+{{ displayCollapsed ? '' : ' Create New Cover Letter' }}
					</won-button>
				</div>
     
				<upload-resume
					v-if="
						![
							'interview-ai',
							'interview-ai-create',
							'interview-ai-interviews',
							'home'
						].includes(activeRoute)
					"
					:collapsed="displayCollapsed"
				/>
                    
				<div
					v-if="activeRoute !== 'home'"
					class="sidebar-divider"
				/>
    
				<div class="flex flex-col gap-0.5">
					<sidebar-menu
						:active-route="activeRoute"
						:menu-items="links.filter((el) => el?.visible ?? true)"
					/>
				</div>
			</div>
            
			<div
				v-if="isAuthenticated()"
				class="flex w-full flex-col gap-1"
			>
				<pdf-feature-usage-left
					:collapsed="displayCollapsed"
				/>
                
				<div class="relative">
					<pdf-upgrade-button-sidebar
						v-tooltip="{
							content: 'Upgrade Plan',
							delay: { show: displayCollapsed ? 500 : 999999, hide: 100 }
						}"
						:collapsed="displayCollapsed"
					/>
				</div>
    
				<div
					class="flex items-center"
					:class="displayCollapsed ? 'justify-center' : 'justify-between'"
				>
					<router-link
						v-tooltip="{ content: 'Log Out', delay: { show: displayCollapsed ? 500 : 999999, hide: 100 } }"
						:to="{ path: '/logout' }"
						class="flex flex-1 cursor-pointer items-center gap-1 rounded-[6px] px-2.5 py-1.5 hover:bg-won-bg-blue"
						:class="displayCollapsed ? 'justify-center' : 'justify-start'"
					>
						<ArrowRightStartOnRectangleIcon class="size-4 text-won-gray-blue" />
						<div
							v-if="!displayCollapsed"
							class="ml-1 font-[Inter] text-sm font-medium text-won-gray-blue"
						>
							Log Out
						</div>
					</router-link>
				</div>
			</div>

			<div
				v-else
				:class="displayCollapsed ? 'justify-center' : 'justify-start'"
				class="
					flex h-10 w-full cursor-pointer items-center gap-1 rounded-[6px]
					px-2.5 py-1.5 hover:bg-won-green-light
				"
				@click="showAuthPopupHandler"
			>
				<UserPlusIcon
					class="size-5 text-won-gray-blue"
				/>

				<div
					v-if="!displayCollapsed"
					class="font-[Inter] text-sm font-medium text-won-gray-blue"
				>
					Sign Up / Log in
				</div>
			</div>
		</div>
	</div>
	<div
		v-if="!collapsed"
		class="absolute z-69 size-full bg-black opacity-70 md:hidden"
		@click="sidebarStore.sidebarCollapseHandler"
	/>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';

import {
	ArrowTopRightOnSquareIcon,
	ArrowRightStartOnRectangleIcon,
} from '@heroicons/vue/24/outline';
import {
	UserPlusIcon
} from '@heroicons/vue/24/solid';
import { setAuthState } from '../composables/auth';
import { useMenuItems } from '../composables/menu-items';

import { useSidebarStore } from '../store/sidebar.store';
import { useSubscriptionPlanStore } from '../store/subscription.store';
import { useFeatureUsageStore } from '../store/feature-usage.store';
import { useAuthPopupStore } from '../store/auth-popup.store';

import WonButton from './won-button.vue';
import UploadResume from './upload-resume.vue';
import SidebarMenu from './sidebar-menu.vue';
import PdfUpgradeButtonSidebar from './pdf-builder/pdf-upgrade-button-sidebar.vue';
import PdfFeatureUsageLeft from './pdf-builder/pdf-feature-usage-left.vue';

const emit = defineEmits(['new-resource']);

const route = useRoute();
const router = useRouter();

const { isAuthenticated } = setAuthState();
const { links } = useMenuItems();

const subscriptionPlanStore = useSubscriptionPlanStore();
const featuresUsageStore = useFeatureUsageStore();
const sidebarStore = useSidebarStore();
const authPopupStore = useAuthPopupStore();

const { collapsed } = storeToRefs(sidebarStore);
const { isSubscribe } = storeToRefs(subscriptionPlanStore);

const displayCollapsed = ref(collapsed.value);
watch(collapsed, (newVal) => {
	if (newVal) {
		displayCollapsed.value = true;
	} else {
		setTimeout(() => { displayCollapsed.value = false; }, 150);
	}
});

const activeRoute = ref(route.name);

const createNewResumeHandler = () => {
	sidebarStore.collapseOnMobile();

	if (isAuthenticated()) {
		emit('new-resource');
	} else {
		authPopupStore.setShowAuthPopup('resumes.edit');
	}
};

const createNewCoverLetterHandler = () => {
	sidebarStore.collapseOnMobile();

	if (isAuthenticated()) {
		emit('new-resource');
	} else {
		authPopupStore.setShowAuthPopup('cover-letters.edit');
	}

};

const startNewInterviewHandler = () => {
	sidebarStore.collapseOnMobile();

	if (isAuthenticated()) {
		if (
			useFeatureUsageStore().fetchFeatureUsage('interview')?.usage == null
			|| (useFeatureUsageStore().fetchFeatureUsage('interview')?.usage === 0 && !isSubscribe.value)
		) {
			featuresUsageStore.setShowLimitExceed('interview');
			return;
		}
	}

	router.push({ name: 'interview-ai-create', force: true });
};

const showAuthPopupHandler = () => {
	sidebarStore.collapseOnMobile();
	authPopupStore.setShowAuthPopup();
};


watch(() => route.name, async (newName) => {
	activeRoute.value = newName;
});

onMounted(() => window.addEventListener('resize', sidebarStore.handleViewportChange));
onUnmounted(() => window.removeEventListener('resize', sidebarStore.handleViewportChange));
</script>

<style>
.sidebar-divider {
    margin: 16px 0;
    width: 100%;
    height: 1px;
    background-color: #E9EEF2;
}
</style>
